import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "smartlink-wrapper font-sans" }
const _hoisted_2 = { class: "popup-header bg-slate-800 text-cyan-400 font-bold px-2 py-1 rounded-t flex justify-between items-center" }
const _hoisted_3 = { class: "text-[10px] text-slate-400 font-normal" }
const _hoisted_4 = { class: "p-2 space-y-1 text-xs text-slate-200 bg-slate-900/90 rounded-b border border-slate-700" }
const _hoisted_5 = { class: "flex justify-between gap-4" }
const _hoisted_6 = { class: "flex justify-between gap-4" }
const _hoisted_7 = { class: "flex justify-between gap-4" }
const _hoisted_8 = { class: "text-amber-400" }
const _hoisted_9 = { class: "flex justify-between gap-4" }
const _hoisted_10 = { class: "text-amber-400" }
const _hoisted_11 = { class: "ui-header p-3 flex items-center justify-between" }
const _hoisted_12 = { class: "flex items-center gap-3" }

import { ref, onMounted, onUnmounted } from 'vue';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

interface Telemetry {
  latency: number;
  rxlvl: number;
  txlvl: number;
  snrh: number;
  snrv: number;
}

interface NetworkNode {
  id: string;
  siteName: string;
  type: 'RAJANT' | 'BASE' | 'CLIENTE';
  pos: { x: number; y: number; z: number };
  telemetry: Telemetry;
  linksTo: string[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Test-EO',
  setup(__props) {

const networkNodes = ref<NetworkNode[]>([
  {
    id: 'CE-RJNT-13',
    siteName: 'CAMIÓN 01',
    type: 'RAJANT',
    pos: { x: -20, y: -2, z: -15 },
    telemetry: { latency: 8.152, rxlvl: -85, txlvl: -85, snrh: 12, snrv: 12 },
    linksTo: ['PMP-CE-RJNT-05', 'BASE-RJNT-ROS']
  },
  {
    id: 'PMP-CE-RJNT-05',
    siteName: 'CAMIÓN 02',
    type: 'CLIENTE',
    pos: { x: 15, y: -8, z: 10 },
    telemetry: { latency: 5.697, rxlvl: -72, txlvl: -70, snrh: 22, snrv: 20 },
    linksTo: ['BASE-RJNT-ROS']
  },
  {
    id: 'BASE-RJNT-ROS',
    siteName: 'TORRE BASE',
    type: 'BASE',
    pos: { x: 35, y: 12, z: -30 },
    telemetry: { latency: 2.110, rxlvl: -60, txlvl: -58, snrh: 30, snrv: 28 },
    linksTo: ['BASE-RJNT-LAGRD']
  },
  {
    id: 'CE-RJNT-17',
    siteName: 'CAMIÓN 03',
    type: 'RAJANT',
    pos: { x: -45, y: 2, z: 20 },
    telemetry: { latency: 18.450, rxlvl: -91, txlvl: -88, snrh: 8, snrv: 7 },
    linksTo: ['CE-RJNT-13']
  },
  {
    id: 'BASE-RJNT-LAGRD',
    siteName: 'CAMIÓN 04',
    type: 'CLIENTE',
    pos: { x: 10, y: -15, z: 40 },
    telemetry: { latency: 4.200, rxlvl: -68, txlvl: -65, snrh: 25, snrv: 24 },
    linksTo: []
  }
]);

const canvasContainer = ref<HTMLDivElement | null>(null);
const hoveredNode = ref<NetworkNode | null>(null);
const popupPos = ref({ x: 0, y: 0 });

let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let controls: OrbitControls;
let raycaster: THREE.Raycaster;
let mouse: THREE.Vector2;
let vehicleGroups: THREE.Group[] = [];
let labelMeshes: THREE.Mesh[] = [];
let animId: number;

// --- DIBUJAR ETIQUETA SOBRE EL VEHÍCULO ---
const createNodeLabelTexture = (type: string, id: string): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 90;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = type === 'BASE' ? '#f97316' : type === 'RAJANT' ? '#0284c7' : '#10b981';
  ctx.roundRect(10, 10, 236, 70, 10);
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(id, 128, 52);

  return new THREE.CanvasTexture(canvas);
};

// --- CREACIÓN DEL VEHÍCULO EN 3D (PROCEDURAL) ---
const createVehicle3D = (nodeData: NetworkNode): THREE.Group => {
  const vehicleGroup = new THREE.Group();

  // Color del vehículo según tipo de equipo
  const mainColor = nodeData.type === 'BASE' ? 0xf97316 : nodeData.type === 'RAJANT' ? 0x0284c7 : 0x10b981;
  const bodyMat = new THREE.MeshStandardMaterial({ color: mainColor, roughness: 0.3 });
  const glassMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.1 });
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, roughness: 0.8 });

  // 1. Chasis Base
  const chassisGeo = new THREE.BoxGeometry(6, 2, 10);
  const chassis = new THREE.Mesh(chassisGeo, bodyMat);
  chassis.position.y = 1.5;
  vehicleGroup.add(chassis);

  // 2. Cabina
  const cabinGeo = new THREE.BoxGeometry(5.2, 2.2, 4.5);
  const cabin = new THREE.Mesh(cabinGeo, bodyMat);
  cabin.position.set(0, 3.6, -1);
  vehicleGroup.add(cabin);

  // Parabrisas / Ventanas
  const windowGeo = new THREE.BoxGeometry(5.3, 1.5, 2);
  const windows = new THREE.Mesh(windowGeo, glassMat);
  windows.position.set(0, 3.8, -0.5);
  vehicleGroup.add(windows);

  // 3. Ruedas (4 ruedas)
  const wheelGeo = new THREE.CylinderGeometry(1.2, 1.2, 1, 16);
  wheelGeo.rotateZ(Math.PI / 2);

  const wheelPositions = [
    { x: -3.2, y: 1.2, z: 3 },
    { x: 3.2, y: 1.2, z: 3 },
    { x: -3.2, y: 1.2, z: -3 },
    { x: 3.2, y: 1.2, z: -3 }
  ];

  wheelPositions.forEach(pos => {
    const wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.position.set(pos.x, pos.y, pos.z);
    vehicleGroup.add(wheel);
  });

  // 4. Antena de Comunicación en el techo
  const poleGeo = new THREE.CylinderGeometry(0.1, 0.1, 3, 8);
  const poleMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const pole = new THREE.Mesh(poleGeo, poleMat);
  pole.position.set(0, 5.5, -2);
  vehicleGroup.add(pole);

  // 5. Etiqueta Flotante (ID del equipo)
  const labelGeo = new THREE.PlaneGeometry(8, 2.8);
  const labelTexture = createNodeLabelTexture(nodeData.type, nodeData.id);
  const labelMat = new THREE.MeshBasicMaterial({ map: labelTexture, transparent: true, side: THREE.DoubleSide });
  const labelMesh = new THREE.Mesh(labelGeo, labelMat);
  labelMesh.position.set(0, 9, 0);
  labelMesh.name = "label"; // Para animar billboarding
  vehicleGroup.add(labelMesh);
  labelMeshes.push(labelMesh);

  // Posicionar todo el grupo en las coordenadas del terreno
  vehicleGroup.position.set(nodeData.pos.x, nodeData.pos.y, nodeData.pos.z);
  vehicleGroup.userData = nodeData; // Guardar telemetría

  return vehicleGroup;
};

// --- INICIALIZAR ESCENA 3D ---
const initThree = () => {
  if (!canvasContainer.value) return;

  const width = canvasContainer.value.clientWidth;
  const height = canvasContainer.value.clientHeight;

  scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0c121e);
  scene.fog = new THREE.FogExp2(0x0c121e, 0.003);

  camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
  camera.position.set(0, 80, 130);

  renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  canvasContainer.value.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;

  raycaster = new THREE.Raycaster();
  mouse = new THREE.Vector2();

  // Luces
  scene.add(new THREE.AmbientLight(0xffffff, 0.8));
  const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
  dirLight.position.set(50, 100, 50);
  scene.add(dirLight);

  // Terreno Mina
  const planeGeo = new THREE.PlaneGeometry(180, 180, 64, 64);
  planeGeo.rotateX(-Math.PI / 2);
  const pos = planeGeo.attributes.position;

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const dist = Math.sqrt(x * x + z * z);
    let y = Math.sin(x * 0.05) * Math.cos(z * 0.05) * 5;
    if (dist < 65) y -= (65 - dist) * 0.4;
    pos.setY(i, y);
  }
  planeGeo.computeVertexNormals();

  const terrainMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.9 });
  scene.add(new THREE.Mesh(planeGeo, terrainMat));

  // --- CREAR VEHÍCULOS 3D EN VEZ DE CUADRADOS ---
  networkNodes.value.forEach(nodeData => {
    const vehicle = createVehicle3D(nodeData);
    scene.add(vehicle);
    vehicleGroups.push(vehicle);
  });

  // --- DIBUJAR ENLACES RF (LÍNEAS DESDE LAS ANTENAS) ---
  networkNodes.value.forEach(sourceNode => {
    sourceNode.linksTo.forEach(targetId => {
      const targetNode = networkNodes.value.find(n => n.id === targetId);
      if (targetNode) {
        const points = [
          new THREE.Vector3(sourceNode.pos.x, sourceNode.pos.y + 7, sourceNode.pos.z),
          new THREE.Vector3(targetNode.pos.x, targetNode.pos.y + 7, targetNode.pos.z)
        ];

        const lineGeo = new THREE.BufferGeometry().setFromPoints(points);
        const isDegraded = sourceNode.telemetry.latency > 15 || sourceNode.telemetry.rxlvl < -88;
        const lineMat = new THREE.LineBasicMaterial({
          color: isDegraded ? 0xef4444 : 0x10b981,
          linewidth: 3
        });

        scene.add(new THREE.Line(lineGeo, lineMat));
      }
    });
  });

  animate();
};

// --- INTERACCIÓN RAYCASTING (HOVER) ---
const onMouseMove = (event: MouseEvent) => {
  if (!canvasContainer.value) return;

  const rect = canvasContainer.value.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  raycaster.setFromCamera(mouse, camera);
  
  // Intersectar con los grupos de vehículos
  const intersects = raycaster.intersectObjects(vehicleGroups, true);

  if (intersects.length > 0) {
    // Subir hasta encontrar el grupo principal que tiene la data
    let obj: THREE.Object3D | null = intersects[0].object;
    while (obj && !obj.userData?.id && obj.parent) {
      obj = obj.parent;
    }

    if (obj && obj.userData?.id) {
      hoveredNode.value = obj.userData as NetworkNode;
      popupPos.value = {
        x: event.clientX + 15,
        y: event.clientY - 20
      };
    }
  } else {
    hoveredNode.value = null;
  }
};

const onClickNode = () => {
  if (hoveredNode.value) {
    console.log('Vehículo Seleccionado:', hoveredNode.value);
  }
};

// --- BUCLE ANIMACIÓN ---
const animate = () => {
  animId = requestAnimationFrame(animate);

  // Mantener las etiquetas flotantes de los vehículos siempre de frente a la cámara
  labelMeshes.forEach(label => {
    label.quaternion.copy(camera.quaternion);
  });

  controls.update();
  renderer.render(scene, camera);
};

onMounted(() => {
  initThree();
});

onUnmounted(() => {
  cancelAnimationFrame(animId);
  renderer?.dispose();
});

return (_ctx: any,_cache: any) => {
  const _component_v_chip = _resolveComponent("v-chip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "canvasContainer",
      ref: canvasContainer,
      class: "canvas-3d",
      onMousemove: onMouseMove,
      onClick: onClickNode
    }, null, 544),
    (hoveredNode.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "telemetry-popup font-mono backdrop-blur",
          style: _normalizeStyle({ left: popupPos.value.x + 'px', top: popupPos.value.y + 'px' })
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", null, _toDisplayString(hoveredNode.value.id) + " (" + _toDisplayString(hoveredNode.value.siteName) + ")", 1),
            _createElementVNode("span", _hoisted_3, "Latencia: " + _toDisplayString(hoveredNode.value.telemetry.latency) + " ms", 1)
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _cache[0] || (_cache[0] = _createElementVNode("span", { class: "text-slate-400" }, "RXLVL:", -1)),
              _createElementVNode("span", {
                class: _normalizeClass(hoveredNode.value.telemetry.rxlvl >= -80 ? 'text-emerald-400' : 'text-red-400')
              }, _toDisplayString(hoveredNode.value.telemetry.rxlvl) + " dBm ", 3)
            ]),
            _createElementVNode("div", _hoisted_6, [
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-slate-400" }, "TXLVL:", -1)),
              _createElementVNode("span", null, _toDisplayString(hoveredNode.value.telemetry.txlvl) + " dBm", 1)
            ]),
            _createElementVNode("div", _hoisted_7, [
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-slate-400" }, "SNRH:", -1)),
              _createElementVNode("span", _hoisted_8, _toDisplayString(hoveredNode.value.telemetry.snrh) + " dB", 1)
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-slate-400" }, "SNRV:", -1)),
              _createElementVNode("span", _hoisted_10, _toDisplayString(hoveredNode.value.telemetry.snrv) + " dB", 1)
            ])
          ])
        ], 4))
      : _createCommentVNode("", true),
    _createElementVNode("header", _hoisted_11, [
      _createElementVNode("div", _hoisted_12, [
        _cache[4] || (_cache[4] = _createElementVNode("h1", { class: "text-lg font-bold text-cyan-400 tracking-wider" }, [
          _createTextVNode(" SMARTLINK V2.6 "),
          _createElementVNode("span", { class: "text-xs font-normal text-slate-300" }, "| MINERA CMDIC")
        ], -1)),
        _createVNode(_component_v_chip, {
          color: "cyan-accent-3",
          size: "small",
          variant: "outlined"
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Equipos Activos: " + _toDisplayString(networkNodes.value.length), 1)
          ]),
          _: 1
        })
      ]),
      _cache[5] || (_cache[5] = _createStaticVNode("<div class=\"flex items-center gap-4 text-xs\" data-v-50c09b2c><span class=\"flex items-center gap-1.5\" data-v-50c09b2c><span class=\"w-2.5 h-2.5 bg-emerald-500 rounded-full\" data-v-50c09b2c></span> Enlace Estable</span><span class=\"flex items-center gap-1.5\" data-v-50c09b2c><span class=\"w-2.5 h-2.5 bg-red-500 rounded-full\" data-v-50c09b2c></span> Degradado / Crítico</span></div>", 1))
    ]),
    _cache[6] || (_cache[6] = _createElementVNode("div", { class: "hint-overlay text-xs text-slate-400" }, " Pasa el cursor sobre un vehículo para desplegar la telemetría en vivo ", -1))
  ]))
}
}

})